import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, PlusCircle } from "lucide-react";
import { opApi, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function Cards({ cards }) {
  if (!cards?.length) return <p className="text-xs text-muted-foreground">No loyalty card yet. Adding points creates one.</p>;
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="loyalty-phone-cards">
      {cards.map((c, i) => (
        <div key={i} className="border border-border p-2.5 text-sm">
          <div className="font-semibold">{c.program}</div>
          <div className="text-xs text-muted-foreground">{c.progress} / {c.goal} {c.earn_type === "stamps" ? "stamps" : "points"} · {c.rewards_ready} reward{c.rewards_ready !== 1 ? "s" : ""} ready</div>
          {c.unlocked?.length > 0 && <div className="text-xs text-primary mt-1">Unlocked: {c.unlocked.join(", ")}</div>}
        </div>
      ))}
    </div>
  );
}

// Staff look up a customer by phone and add points/stamps, no order needed.
export function AddPointsByPhone() {
  const [f, setF] = useState({ phone: "", amount: 1, name: "", email: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const lookup = async () => {
    setBusy(true);
    try {
      const { data } = await opApi.get("/tenant/loyalty/lookup", { params: { phone: f.phone } });
      setRes(data);
      if (data.customer) setF((x) => ({ ...x, name: x.name || data.customer.name || "", email: x.email || data.customer.email || "" }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await opApi.post("/tenant/loyalty/add-points", { ...f, amount: Number(f.amount) });
      setRes(data);
      toast.success(`Added ${f.amount} to ${data.customer.name || data.customer.phone}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border p-4 mb-6 space-y-3" data-testid="loyalty-add-by-phone">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider">Add points by phone</h3>
        <p className="text-xs text-muted-foreground">For visits without an online order. New numbers get a card automatically. Add an email so the customer can sign in on your website to see their points.</p>
      </div>
      <div className="grid sm:grid-cols-[1fr_90px_auto] gap-2">
        <Input className="rounded-none" placeholder="Phone number" value={f.phone} onChange={set("phone")} onKeyDown={(e) => e.key === "Enter" && lookup()} data-testid="loyalty-phone-input" />
        <Input className="rounded-none" type="number" min="1" value={f.amount} onChange={set("amount")} data-testid="loyalty-amount-input" />
        <Button variant="outline" className="rounded-none gap-1.5" disabled={busy || f.phone.length < 7} onClick={lookup} data-testid="loyalty-lookup-btn"><Search className="h-4 w-4" /> Look up</Button>
      </div>
      {res && (
        <div className="space-y-3" data-testid="loyalty-phone-result">
          {!res.customer && <p className="text-xs" data-testid="loyalty-phone-new">New customer. Add a name and email (optional).</p>}
          <div className="grid sm:grid-cols-2 gap-2">
            <Input className="rounded-none" placeholder="Name (optional)" value={f.name} onChange={set("name")} data-testid="loyalty-name-input" />
            <Input className="rounded-none" type="email" placeholder="Email (optional, lets them sign in)" value={f.email} onChange={set("email")} data-testid="loyalty-email-input" />
          </div>
          <Cards cards={res.cards} />
        </div>
      )}
      <Button className="rounded-none gap-1.5" disabled={busy || f.phone.length < 7 || !(Number(f.amount) > 0)} onClick={add} data-testid="loyalty-add-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />} Add {f.amount || 0}
      </Button>
    </div>
  );
}
